import React, {
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
} from 'react'
import { SeatCard } from '../SeatCard/SeatCard'
import {
    getSeatsData,
    InterknowlogyData,
    SeatData,
    transformAreaData,
} from '../data'
import { ElectionDataFetchError, ElectionTitle } from '../components'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { TransformedData } from '@west-australian-newspapers/election-api-types'
import {
    TheSeatsWidgetChevron,
    TheSeatsWidgetContainer,
    TheSeatsWidgetHeaderLink,
    TheSeatsWidgetSeatGrid,
    TheSeatsNoResultsText,
    TheSeatsDivider,
} from './TheSeatsWidget.styled'
import {
    AllEvents,
    DataLayerEventName,
    NavEvent,
    useQueryParams,
} from '@news-mono/web-common'
import { useImpressionAvailable } from '../../../__helpers/impression-available-helper'
import { ElectionSearchInput } from '../components/ElectionSearch'

export interface SeatsWidgetProps {
    data: MaybeLoaded<TransformedData>
    onEvent: (event: AllEvents) => void
    electoratePageUrl: string
    seatDisplayCount?: number
}

export const TheSeatsWidget = ({
    data,
    onEvent,
    electoratePageUrl,
    seatDisplayCount = 6,
}: SeatsWidgetProps): JSX.Element => {
    const [searchTerm, setSearchTerm] = React.useState<string>('')
    const { queryParams, pathname } = useQueryParams()

    const originalSeatData = useMemo(() => {
        if (!data.loaded) return []
        const interknowlogyData = transformAreaData(
            data.result as InterknowlogyData,
        )
        return getSeatsData(interknowlogyData).slice(0, seatDisplayCount)
    }, [data, seatDisplayCount])

    const [seatData, setSeatData] = React.useState<SeatData[]>(originalSeatData)

    /** Filters the Seat Data based on Candidate Name or Electorate */
    const filterResults = useCallback(
        (searchTerm: string, seatData: SeatData[]) => {
            setSearchTerm(searchTerm)
            return seatData.filter((seat) => {
                return (
                    seat.seatName
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    seat.candidates.find((candidate) =>
                        candidate.candidateName
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()),
                    )
                )
            })
        },
        [],
    )

    const handleSearch = useCallback(
        (searchTerm: string) => {
            const filteredData = filterResults(searchTerm, originalSeatData)
            setSeatData(filteredData)
        },
        [filterResults, originalSeatData],
    )

    useEffect(() => {
        // Dont do anything if the data has not loaded
        if (!data.loaded) return

        // Check for search query param on load
        const searchParams = queryParams.get('search')

        // If we have a search query param, filter the data based on it
        const filteredData = searchParams
            ? filterResults(searchParams, originalSeatData)
            : originalSeatData
        // Update state
        setSeatData(filteredData)
    }, [data, filterResults, queryParams, originalSeatData])

    // Loading State!
    if (!data.loaded) {
        return (
            <>
                {Array(4) // Render 4 skeletons while loading
                    .fill(0)
                    .map((_, index) => (
                        <SeatCard key={index} isLoading />
                    ))}
            </>
        )
    }
    // Error State! (data has loaded, but there is no data)
    if (!data.result) {
        return (
            <TheSeatsWidgetWrapper
                onEvent={onEvent}
                electoratePageUrl={electoratePageUrl}
            >
                <ElectionDataFetchError />
            </TheSeatsWidgetWrapper>
        )
    }

    // No Search Results State!
    if (searchTerm && seatData && seatData.length <= 0) {
        return (
            <TheSeatsWidgetWrapper
                onEvent={onEvent}
                electoratePageUrl={electoratePageUrl}
            >
                <ElectionSearchInput
                    onEvent={onEvent}
                    queryParams={queryParams}
                    pathname={pathname}
                    onSearch={handleSearch}
                />
                <TheSeatsNoResultsText>
                    No results for ‘{searchTerm}’
                </TheSeatsNoResultsText>
                <TheSeatsDivider />
            </TheSeatsWidgetWrapper>
        )
    }

    return (
        <TheSeatsWidgetWrapper
            onEvent={onEvent}
            electoratePageUrl={electoratePageUrl}
        >
            <ElectionSearchInput
                onEvent={onEvent}
                queryParams={queryParams}
                pathname={pathname}
                onSearch={handleSearch}
            />
            <TheSeatsWidgetSeatGrid>
                {seatData.map((seat) => (
                    <SeatCard
                        key={seat.seatId}
                        seat={seat}
                        onEvent={onEvent}
                        electoratePageUrl={electoratePageUrl}
                    />
                ))}
            </TheSeatsWidgetSeatGrid>
        </TheSeatsWidgetWrapper>
    )
}

const TheSeatsWidgetWrapper = ({
    children,
    onEvent,
    electoratePageUrl,
}: PropsWithChildren<{
    onEvent: (event: NavEvent) => void
    electoratePageUrl: string
}>) => {
    const impressionAvailableRef = useImpressionAvailable({
        loading: false,
        available: useCallback(() => {
            onEvent({
                type: DataLayerEventName.navAvailable,
                originator: 'ElectionTheSeatsWidget',
                payload: {
                    navName: 'ElectionSeats',
                },
            })
        }, [onEvent]),
    })

    const handleHeadingClick = useCallback(() => {
        onEvent({
            type: DataLayerEventName.navClicked,
            originator: 'ElectionTheSeatsWidget',
            payload: {
                navName: 'ElectionSeatsWidget',
                navLocation: 'OnPage',
                navText: 'The Seats',
                navLink: electoratePageUrl,
            },
        })
    }, [electoratePageUrl, onEvent])
    return (
        <TheSeatsWidgetContainer ref={impressionAvailableRef}>
            <TheSeatsWidgetHeaderLink
                to={electoratePageUrl}
                onClick={handleHeadingClick}
            >
                <ElectionTitle>The Seats</ElectionTitle>
                <TheSeatsWidgetChevron />
            </TheSeatsWidgetHeaderLink>
            {children}
        </TheSeatsWidgetContainer>
    )
}
